<!-- eslint-disable max-len -->
<template lang="pug">
.container.document-view
  .breadcrumb-voucher.py-3
    nav(aria-label='breadcrumb')
      ol.breadcrumb
        li.breadcrumb-item
          router-link(to='/')
            Icon.color--info(name='house')
        li.breadcrumb-item
          router-link.color--info(:to='`/`')
            span Emissão de certificado
        li.breadcrumb-item.active
          router-link.color--primary.disabled.fw-600(to='/') Envio de arquivos
  Wizzard(current-page='alldone', role='nav')
  h2.fw-600 Agora vamos precisar de alguns documentos
  .main.gap-4(role='main')
    .d-flex.flex-column.col-md-12.col-lg-6
      p Selecione o tipo de empresa para ver a lista de documentos.
      Selectable(
        name='companySegment',
        icon='building',
        :options='form.types',
        v-model='companySegmentSeleted',
        labelSelectable='nome',
        placeholder='Selecione'
      )
      Selectable.mt-4(
        v-show='!!companySegmentSeleted',
        name='companySegment',
        icon='building',
        :options='form.documents',
        v-model='companyTypeSelected',
        labelSelectable='nome',
        placeholder='Selecione um tipo de unidade'
      )
      .text-documents-required(
        :class='[companyTypeSelected !== "" ? "d-flex" : "d-none"]'
      )
        h4.h-x-small.ps-0 {{ companyType.nome }}
        hr
        ul
          li.ps-1(v-for='doc in companyType.list')
            span {{ doc }}
        ul.notes-list.p-0(v-if='hasNotes')
          li(v-for='nota in companyType.notas')
            span #[strong Nota:] {{ nota }}
    .uploads.w-100
      Upload.element-sticky.flex-grow-1(
        :is-mobile='isMobile',
        :files-loaded='filesLoaded',
        :extension-types='extentionsAllows',
        @addFile='handleAddFile'
      )
      FilesLoader(
        :files-loaded='filesLoaded',
        :extension-types='extentionsAllows',
        @handleDeleteFile='handleDeleteFile'
      )
  .d-flex.justify-content-end.action-section.flex-row
    button.btn(
      type='button',
      :class='isInvalid ? "btn-secondary disabled" : "btn-primary"',
      @click='sendFiles',
      :disabled='isInvalid'
    )
      span.me-2 Avançar
      Icon(name='arrow-right')
  Contact(style='margin-bottom: 6rem')
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import Icon from '@/components/Atoms/Icon.vue'
  import Contact from '@/components/Molecules/Contact.vue'
  import Wizzard from '@/components/Molecules/Wizzard.vue'
  import Selectable from '@/components/Atoms/Selectable.vue'
  import Upload from '@/components/Molecules/Upload.vue'
  import Camera from '@/components/Molecules/Camera.vue'
  import { typesCompany, documentsList } from '@/store/JSON/documentsJson'
  import mobileAndTabletCheck from '@/utils/browserDeviceCheck'
  import { mapActions, mapState } from 'pinia'
  import { useToastGlobal } from '@/store/Pinia/useToastGlobal.store'
  import sentFilesController from '@/infra/sisAr/sentFiles.controller'
  import { useSolicitation } from '@/store/Pinia/useSolicitation.store'
  import { useModalLoading } from '@/store/Pinia/useModalLoading.store'
  import FilesLoader from '@/components/Molecules/FilesLoader.vue'
  import { useOeRecaptcha } from '@/composables/useOeRecaptcha'

  type DocumentsSegment =
    | 'Sociedade'
    | 'Empresário Individual'
    | 'Das demais Entidades de Direito Privado'
    | 'Das Entidades de Direito Público'

  interface IObjectDocument {
    nome: string
    type: DocumentsSegment
    list: string[]
    notas: string[]
  }

  export default defineComponent({
    name: 'DocumentsUpload',
    components: {
      Icon,
      Wizzard,
      Selectable,
      Contact,
      Camera,
      Upload,
      FilesLoader,
    },
    setup() {
      return {
        oeRecaptcha: useOeRecaptcha(),
      }
    },
    data: () => ({
      form: {
        types: typesCompany,
        documents: [] as Array<IObjectDocument>,
      },
      companySegmentSeleted: '',
      companyTypeSelected: '',
      companyType: {} as IObjectDocument,
      isMobile: false,
      filesLoaded: [] as Array<File>,
      extentionsAllows: ['png', 'jpg', 'jpeg', 'doc', 'docs', 'docx', 'pdf'],
    }),
    watch: {
      companySegmentSeleted(curr: string) {
        this.form.documents = documentsList.filter(
          (x: IObjectDocument) => x.type === curr,
        )
      },
      companyTypeSelected(curr) {
        const company = this.form.documents.find(
          (x: IObjectDocument) => x.nome === curr,
        )
        if (company) {
          this.companyType = company
        }
      },
    },
    computed: {
      ...mapState(useSolicitation, ['getSolicitation']),
      isInvalid() {
        return this.filesLoaded.length === 0 || this.findDocumentError()
      },
      hasNotes() {
        return this.companyType.notas && this.companyType.notas.length > 0
      },
    },
    methods: {
      ...mapActions(useToastGlobal, ['toggleToastGlobal']),
      ...mapActions(useModalLoading, ['setIsLoading']),
      handleAddFile(newFiles: File[]) {
        if (this.filesLoaded.length >= 10) {
          return
        }
        if (this.filesLoaded.length > 0) {
          this.filesLoaded = [...this.filesLoaded, ...newFiles]
        } else {
          this.filesLoaded = [...newFiles]
        }
      },
      handleDeleteFile(indexFile: number) {
        const filtered = this.filesLoaded.filter(
          (x: File, i: number) => i !== indexFile,
        )
        this.filesLoaded = filtered
      },
      async sendFiles() {
        try {
          this.setIsLoading({
            isLoading: true,
            title: 'Aguarde enquanto enviamos seus documentos',
            message: '',
            image: 'fileUpload',
          })

          await sentFilesController.sendFiles(
            this.filesLoaded,
            this.oeRecaptcha.getToken,
          )

          if (this.getSolicitation.isSSL) {
            this.$router.push('/domain-validation')
          } else {
            this.$router.push('/success-upload-files')
          }
        } catch (error: any) {
          const backendMessage =
            error?.response?.data?.message ||
            error?.response?.data?.mensagem ||
            error?.message

          this.toggleToastGlobal({
            isShow: true,
            message:
              backendMessage ||
              'Houve um erro ao enviar seus documentos, tente novamente mais tarde',
          })
        } finally {
          this.setIsLoading({ isLoading: false })
        }
      },
      findDocumentError() {
        return !!this.filesLoaded.find(
          (x: File) => !this.extentionsAllows.includes(x.type.split('/')[1]),
        )
      },
    },
    mounted() {
      this.oeRecaptcha.showBadge()

      this.isMobile = mobileAndTabletCheck()
    },
    beforeRouteEnter(to, from, next) {
      const solicitationStore = useSolicitation()

      if (!solicitationStore.getSolicitation.cdSolicitacao) {
        return next('/')
      }
      return next()
    },
    beforeRouteLeave() {
      this.oeRecaptcha.hideBadge()
    },
  })
</script>

<style lang="stylus" scoped>

  .text-documents-required
    display: flex;
    flex-direction: column
    margin-top: 1rem;
    padding: 1rem;
    border-radius: 2rem;
    background-color: var(--color-grayscale-light)
  .document-view
    padding: 1rem
    text-align: left !important;

  .element-sticky
    position: -webkit-sticky; /* Safari */
    position: sticky;
    top: 1rem;
  .h-x-small
    padding-top: 1rem
    padding-left: 1rem

  .notes-list
    list-style-type: none;
  .main
    position: relative
    margin-top: 2.5rem;
    display: flex;
    margin-bottom: 1rem

  @media screen and (max-width: 990px)
    .main
      flex-direction: column
    .action-section > button
      width: 100%
</style>
